
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, MessageSquare, Heart, GraduationCap, Lock, Briefcase } from "lucide-react";
import { useEffect, useState } from "react";

interface ChatHeaderProps {
  remainingMessages: number;
  remainingVoiceMessages: number;
  isVoiceMode: boolean;
  setIsVoiceMode: (value: boolean) => void;
  dailyMessageLimit: number;
  selectedBot: string;
  onBotChange: (botId: string) => void;
}

const bots = [
  {
    id: "01394a4e-bac9-4d73-a907-ffb6d7891f37",
    title: "Mai Bot chat",
    icon: MessageSquare,
    color: "#0EA5E9", // Ocean Blue
    requiresPlan: false,
  },
  {
    id: "d7cee762-50ec-4eae-8394-729c19e67597",
    title: "Mai Bot tư vấn tình yêu",
    icon: Heart,
    color: "#D946EF", // Magenta Pink
    requiresPlan: true,
  },
  {
    id: "58369626-1d4b-4cbf-b4af-556678fdc94f",
    title: "Mai Bot dạy học",
    icon: GraduationCap,
    color: "#8B5CF6", // Vivid Purple
    requiresPlan: true,
  },
  {
    id: "4dd2e2e0-61db-4942-b0a7-649ac4818782",
    title: "Mai Bot trợ lý công việc",
    icon: Briefcase,
    color: "#9b87f5", // Primary Purple
    requiresPlan: true,
  },
];

export const ChatHeader = ({
  remainingMessages,
  remainingVoiceMessages,
  isVoiceMode,
  setIsVoiceMode,
  dailyMessageLimit,
  selectedBot,
  onBotChange,
}: ChatHeaderProps) => {
  const isMobile = useMediaQuery("(max-width: 924px)");
  const navigate = useNavigate();
  const [userPlan, setUserPlan] = useState<string>("free");
  const [messageLimit, setMessageLimit] = useState<number>(30);

  const selectedBotData = bots.find(bot => bot.id === selectedBot);

  useEffect(() => {
    const fetchUserPlan = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('plan')
          .eq('id', session.user.id)
          .single();
        
        if (profile) {
          setUserPlan(profile.plan);
          const { data: limit } = await supabase.rpc('get_plan_message_limit', {
            user_id_param: session.user.id
          });
          setMessageLimit(limit || 30);
        }
      }
    };

    fetchUserPlan();
  }, []);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/");
    } catch (error) {
      toast.error("Có lỗi xảy ra khi đăng xuất");
    }
  };

  const handleBotSelect = (botId: string) => {
    const bot = bots.find(b => b.id === botId);
    if (bot?.requiresPlan && userPlan === "free") {
      toast.error("Vui lòng nâng cấp gói Basic để sử dụng bot này");
      return;
    }
    onBotChange(botId);
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 mb-2 sm:mb-4 px-2 sm:px-0">
      <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
        <img 
          src="/lovable-uploads/37369634-e5ff-4200-b7aa-0ece5a8baa04.png" 
          alt="Mai Bot Logo" 
          className="w-8 h-8 sm:w-10 sm:h-10"
        />
        <div className="flex flex-col flex-1 sm:flex-none">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="p-0 h-auto hover:bg-transparent text-lg sm:text-2xl font-bold text-foreground flex items-center gap-2 justify-start w-full sm:w-auto"
              >
                {selectedBotData && (
                  <selectedBotData.icon 
                    className="w-5 h-5 sm:w-6 sm:h-6" 
                    color={selectedBotData.color}
                  />
                )}
                {selectedBotData?.title || "Mai Bot"}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align={isMobile ? "start" : "start"} 
              className="w-[calc(100vw-1rem)] sm:w-[250px] max-w-[250px]"
            >
              {bots.map((bot) => (
                <DropdownMenuItem
                  key={bot.id}
                  onClick={() => handleBotSelect(bot.id)}
                  className={`flex items-center gap-2 ${bot.requiresPlan && userPlan === "free" ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={bot.requiresPlan && userPlan === "free"}
                >
                  <bot.icon className="w-4 h-4" color={bot.color} />
                  <span>{bot.title}</span>
                  {bot.requiresPlan && userPlan === "free" && (
                    <Lock className="w-4 h-4 ml-auto text-muted-foreground" />
                  )}
                </DropdownMenuItem>
              ))}
              {userPlan === "free" && (
                <div className="px-2 py-1.5 text-sm text-muted-foreground border-t">
                  Nâng cấp để truy cập các bot khác
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex flex-col text-xs sm:text-sm text-muted-foreground">
            <span>Còn {remainingMessages}/{messageLimit} tin nhắn hôm nay</span>
            {isVoiceMode && !isMobile && (
              <span>Còn {remainingVoiceMessages} tin nhắn thoại hôm nay</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
        {!isMobile && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Chế độ thoại</span>
            <Switch
              checked={isVoiceMode}
              onCheckedChange={setIsVoiceMode}
            />
          </div>
        )}
      </div>
    </div>
  );
};
